// @flow
import * as React from 'react';
import invariant from 'invariant';
import { DateTime } from 'luxon';
import { getUserTimezone, getTimezoneFromLatLong } from 'utils/timezones';
import { toSemanticFilter } from 'utils/search/sidefilters';
import { isObject, objectEmpty, getIn, sum } from 'utils/functional';
import Flex from 'components/Flex';
import Label from 'components/Label';
import Box from 'components/Box';
import DatePicker from 'components/DatePicker';
import SearchFilterSection from './SearchFilterSection';

const { useEffect, useState } = React;

const ONE_DAY_SEC = 24 * 60 * 60;

const toSecondsTimestamp = (dateStr: ?string, timezone: string) => {
  if (!dateStr) return;
  let isoDateStr;
  if (dateStr.includes('/')) {
    const [endMonth, endDay, endYear] = dateStr.split('/');
    isoDateStr = `${endYear}-${
      parseInt(endMonth, 10) < 10 ? `0${endMonth}` : endMonth
    }-${parseInt(endDay, 10) < 10 ? `0${endDay}` : endDay}`;
  } else {
    isoDateStr = dateStr;
  }
  return DateTime.fromISO(isoDateStr, { zone: timezone }).ts / 1000;
};

const fromSecondsTimestamp = (unixTs: number, timezone: string) => {
  if (!unixTs) return;
  return DateTime.fromMillis(unixTs * 1000, { zone: timezone }).toLocaleString({
    ...DateTime.DATE_SHORT,
    timeZone: timezone
  });
};

const offsetToEOD = (ts: ?number) => (ts ? ts + ONE_DAY_SEC - 1 : null);

// Equality that allows empty strings / null / undefined to all be equal
const looseEquals = <T>(a: ?T, b: ?T) => {
  const a2 = typeof a === 'number' ? a : Boolean(a);
  const b2 = typeof b === 'number' ? a : Boolean(b);

  return a2 || b2 ? a === b : true;
};

const reduxTimestampToDateString = (ts, timezone) =>
  ts ? fromSecondsTimestamp(ts, timezone) : '';

export default ({
  facet,
  filters,
  setSearchFilter,
  expanded,
  searchLocation,
  searchLocationActive,
  toggleExpanded
}: {
  facet: SearchFacet,
  filters: SearchFilters | SearchFiltersWithType<string>,
  setSearchFilter: (string, mixed) => void,
  expanded: boolean,
  searchLocation: ?SearchLocation,
  searchLocationActive: boolean,
  toggleExpanded: () => void
}) => {
  invariant(facet.names, 'date facet must have names');
  const names = facet.names;
  const [startName, endName] = names;

  const filter: { [string]: { [string]: string | number } } = (filters[
    facet.id
  ]: any);

  const [startValue, endValue] = names.map(
    n =>
      !objectEmpty(filter) &&
      isObject(filter) &&
      filter[n] &&
      filter[n].value &&
      Number(filter[n].value)
  );

  const count = sum(names.map(n => (getIn(filter, [n, 'value']) ? 1 : 0)));
  const timezone =
    searchLocationActive &&
    searchLocation &&
    searchLocation.latitude &&
    searchLocation.longitude
      ? getTimezoneFromLatLong(
          searchLocation.latitude,
          searchLocation.longitude
        )
      : getUserTimezone();

  const [prevTimezone, setPrevTimezone] = useState(timezone);
  const [prevSearchLocationActive, setPrevSearchLocationActive] = useState(
    searchLocationActive
  );

  const [startDateStr, setStartDateStr] = useState(
    reduxTimestampToDateString(startValue, timezone)
  );

  const [endDateStr, setEndDateStr] = useState(
    reduxTimestampToDateString(endValue, timezone)
  );

  useEffect(
    () => {
      if (
        timezone !== prevTimezone &&
        searchLocationActive !== prevSearchLocationActive
      ) {
        const start = toSecondsTimestamp(startDateStr, timezone);
        const end = offsetToEOD(toSecondsTimestamp(endDateStr, timezone));
        if (!looseEquals(start, startValue) || !looseEquals(end, endValue)) {
          setSearchFilter(
            facet.id,
            toSemanticFilter({
              startName,
              endName,
              startValue: start,
              endValue: end
            })
          );
        }
      } else {
        const newEndDateStr = reduxTimestampToDateString(endValue, timezone);
        const newStartDateStr = reduxTimestampToDateString(
          startValue,
          timezone
        );

        setStartDateStr(newStartDateStr);
        setEndDateStr(newEndDateStr);
      }
      setPrevTimezone(timezone);
      setPrevSearchLocationActive(searchLocationActive);
    },
    [
      prevSearchLocationActive,
      prevTimezone,
      searchLocationActive,
      timezone,
      startDateStr,
      endDateStr,
      endName,
      endValue,
      startName,
      startValue,
      facet.id,
      setSearchFilter
    ]
  );

  const onChangeStart = (dateStr: ?string) => {
    setSearchFilter(
      facet.id,
      toSemanticFilter({
        startName,
        endName,
        endValue,
        startValue: dateStr && toSecondsTimestamp(dateStr, timezone)
      })
    );
  };

  const onChangeEnd = (dateStr: ?string) => {
    setSearchFilter(
      facet.id,
      toSemanticFilter({
        startName,
        endName,
        startValue,
        endValue: dateStr && offsetToEOD(toSecondsTimestamp(dateStr, timezone))
      })
    );
  };

  return (
    <SearchFilterSection
      title={facet.title}
      expanded={expanded}
      toggleExpanded={toggleExpanded}
      data-qa-id={`side-filter-facet-${facet.name}`}
      data-qa-filter-value={[startValue, endValue].join()}
      count={count}
      noMask
    >
      <Flex flexDirection="column">
        <Box mt="10px">
          <Label data-qa-id="actiondate-starts-label">
            {getText('Start Date')}
          </Label>
          <DatePicker
            value={startDateStr}
            onChange={onChangeStart}
            placeholder={getText('Select')}
            name="actiondate-starts"
            disablePastDates
            isTimeZoneAware
          />
        </Box>
        <Box mt="10px">
          <Label>{getText('End Date')}</Label>
          <DatePicker
            value={endDateStr}
            onChange={onChangeEnd}
            placeholder={getText('Select')}
            name="actiondate-ends"
            disablePastDates
            isTimeZoneAware
          />
        </Box>
      </Flex>
    </SearchFilterSection>
  );
};
